import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { AutoschedulerDrawStartModes } from '../../../utils/constants';
import Img from '../../../components/Img';
import Popup from '../../../components/Popup';
import { autoScheduleVirtualDraws, clearAllVirtualDrawsForPlan } from '../../../actions/day-planner-actions';
import VirtualDrawListItem from './VirtualDrawListItem';

import styles from './dayPlanner.scss';
import { primaryViews } from '../../../utils/dayPlanner';

const VirtualDrawsList = (props) => {
    const {
        className,
        defaultDrawStartMode,
        onClickAddDivisions,
    } = props;

    const dispatch = useDispatch();
    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);
    const [showAddDayConfig, setShowAddDayConfig] = useState(false);
    const [showConfirmAutoSchedule, setShowConfirmAutoSchedule] = useState(false);
    const [showConfirmDeleteAllVirtualDraws, setShowConfirmDeleteAllVirtualDraws] = useState(false);
    const [drawStartMode, setDrawStartMode] = useState(defaultDrawStartMode);

    const hasVirtualDraws = dayPlanner?.dayPlan?.virtualDraws.length > 0;

    const onClickScheduleIcon = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setDrawStartMode(defaultDrawStartMode);
        setShowConfirmAutoSchedule(true);
    };

    const onClickClearAll = () => {
        setShowConfirmDeleteAllVirtualDraws(true);
    };

    const onConfirmAutoSchedule = () => {
        dispatch(autoScheduleVirtualDraws(drawStartMode));
        onCloseConfirmAutoSchedule();
    };

    const onCloseConfirmAutoSchedule = () => {
        setShowConfirmAutoSchedule(false);
    };

    const onConfirmDeleteAllVirtualDraws = () => {
        dispatch(clearAllVirtualDrawsForPlan());
        onCloseConfirmDeleteAllVirtualDraws();
    };

    const onCloseConfirmDeleteAllVirtualDraws = () => {
        setShowConfirmDeleteAllVirtualDraws(false);
    };

    const onAddDivisionsClick = (ev) => {
        if (dayPlanner?.dayConfig) {
            onClickAddDivisions(ev);
        } else {
            setShowAddDayConfig(true);
        }
    };

    const onCloseAddDayConfigPopup = () => {
        setShowAddDayConfig(false);
    };

    const setUseExactStartTimes = () => {
        setDrawStartMode(AutoschedulerDrawStartModes.UseExactStartTimes);
    };

    const setUseSuggestedStartTimes = () => {
        setDrawStartMode(AutoschedulerDrawStartModes.UseSuggestedStartTimes);
    };

    return (
        <>
            <div className={className}>
                <div className={`${styles.header} d-flex align-items-center`}>
                    <span className="mr-auto" data-testid="day-planner.sidebar.title">{dayPlanner?.viewState?.primaryView === primaryViews.CapacitySimulator ? 'Divisions' : 'Draws'}</span>
                    <button
                        className={`btn ${styles.actionButton}`}
                        type="button"
                        onClick={onClickScheduleIcon}
                        disabled={!hasVirtualDraws}
                        data-testid="day-planner.sidebar.schedule-btn"
                    >
                        <Img src="/img/v3/icons/icon-18px-schedule-white.svg" />
                    </button>
                </div>
                <div className={`${styles.content}`}>
                    <div className={styles.contentSection}>
                        <div className="d-flex">
                            <button
                                className={`btn btn-flex p0 ${styles.addDivisionButton}`}
                                type="button"
                                onClick={onAddDivisionsClick}
                                data-testid="day-planner.sidebar.add-draws-btn"
                            >
                                <Img className="mr4 mb2" src="/img/v3/icons/icon-18px-circle-plus-teal.svg" />
                                {dayPlanner?.viewState?.primaryView === primaryViews.CapacitySimulator ? 'Add divisions' : 'Add draws'}
                            </button>
                            <div className="flex-1" />
                            {hasVirtualDraws && (
                                <button
                                    className={`btn btn-flex p0 ${styles.addDivisionButton}`}
                                    type="button"
                                    onClick={onClickClearAll}
                                    data-testid="day-planner.sidebar.clear-all-btn"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    </div>
                    {dayPlanner?.dayPlan?.virtualDraws?.map((draw) => {
                        return (
                            <VirtualDrawListItem
                                key={draw.id}
                                draw={draw}
                                primaryView={dayPlanner?.viewState?.primaryView}
                                className={styles.contentSection}
                                isDrawLevel={true}
                                data-testid={`day-planner.draw-${draw.id}`}
                            />
                        );
                    })}
                </div>
            </div>
            {showConfirmAutoSchedule && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmPopup}
                    onCloseClick={onCloseConfirmAutoSchedule}
                    closeOnClickOutside={false}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                    data-testid="modal.day-planner.auto-schedule"
                >
                    <div className={styles.title} data-testid="modal.day-planner.auto-schedule.title">
                        Auto schedule?
                    </div>
                    <p className={styles.description} data-testid="modal.day-planner.auto-schedule.description">
                        Do you want to auto schedule?
                    </p>
                    <p className={styles.description}>
                        <input
                            name="drawStartTimes"
                            type="radio"
                            id="useExactStartTimes"
                            checked={drawStartMode === AutoschedulerDrawStartModes.UseExactStartTimes}
                            onClick={setUseExactStartTimes}
                            data-testid="modal.day-planner.auto-schedule.exact-times-radio"
                        />
                        <label htmlFor="useExactStartTimes" data-testid="modal.day-planner.auto-schedule.exact-times-label">
                            &nbsp;Use exact draw start times
                        </label>
                        <br />
                        <input
                            name="drawStartTimes"
                            type="radio"
                            id="useSuggestedStartTimes"
                            checked={drawStartMode === AutoschedulerDrawStartModes.UseSuggestedStartTimes}
                            onClick={setUseSuggestedStartTimes}
                            data-testid="modal.day-planner.auto-schedule.suggested-times-radio"
                        />
                        <label htmlFor="useSuggestedStartTimes" data-testid="modal.day-planner.auto-schedule.suggested-times-label">
                            &nbsp;Use UTR's suggested draw start times
                        </label>
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCloseConfirmAutoSchedule}
                            data-testid="modal.day-planner.auto-schedule.cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmAutoSchedule}
                            disabled={drawStartMode === AutoschedulerDrawStartModes.Invalid}
                            data-testid="modal.day-planner.auto-schedule.confirm-btn"
                        >
                            Schedule
                        </button>
                    </div>
                </Popup>
            )}
            {showConfirmDeleteAllVirtualDraws && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmPopup}
                    onCloseClick={onCloseConfirmDeleteAllVirtualDraws}
                    closeOnClickOutside={false}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                    data-testid="modal.day-planner.delete-all-draws"
                >
                    <p className={styles.description} data-testid="modal.day-planner.delete-all-draws.description">
                        Do you want to delete all virtual draws?
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCloseConfirmDeleteAllVirtualDraws}
                            data-testid="modal.day-planner.delete-all-draws.cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmDeleteAllVirtualDraws}
                            data-testid="modal.day-planner.delete-all-draws.confirm-btn"
                        >
                            Delete
                        </button>
                    </div>
                </Popup>
            )}
            {showAddDayConfig && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmPopup}
                    onCloseClick={onCloseAddDayConfigPopup}
                    closeOnClickOutside={false}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                    data-testid="modal.day-planner.no-day-config"
                >
                    <p className={styles.description} data-testid="modal.day-planner.no-day-config.description">
                        Please add day config before adding divisions for the day.
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onCloseAddDayConfigPopup}
                            data-testid="modal.day-planner.no-day-config.ok-btn"
                        >
                            OK
                        </button>
                    </div>
                </Popup>
            )}
        </>
    );
};

VirtualDrawsList.propTypes = {
    className: PropTypes.string,
    defaultDrawStartMode: PropTypes.string,
    onClickAddDivisions: PropTypes.func.isRequired,
};

VirtualDrawsList.defaultProps = {
    className: '',
    defaultDrawStartMode: AutoschedulerDrawStartModes.Invalid,
};

export default VirtualDrawsList;
